/**
 * ONE push-notification click → in-app route mapping.
 *
 * Single source of truth for the PAGE side of the click flow: on
 * `notificationclick` the service worker relays `ONE_PUSH_CLICK` (with the
 * push `payload.data`) to an existing ONE window, and
 * `PushNotificationsManager` uses this mapping to deep-route it (the "warm"
 * path). The "cold" path — no ONE window open, the SW must `openWindow` a
 * URL — lives in `public/firebase-messaging-sw.js`, which is served unbundled
 * and CANNOT import this module; it mirrors this rule inline. Keep the two in
 * sync.
 *
 * Mapping decision: all three ONE categories are guardian pushes
 * anchored on a ReunificationEvent, and `payload.data.reunification_event_id`
 * is that event's `public_id`
 * (server/apps/reunification/push_notifications.py) — exactly the param the
 * `/events/:eventId` route (pages/EventDetail) consumes. ONE has no more
 * specific per-category routed surface today (PickupDelegationView /
 * StudentCheckInView / CarRiderQueueView are components inside
 * pages/Students.tsx, not routes), so the event detail is the deep target for
 * every category. A payload with no resolvable target returns `null`: the
 * warm path then just keeps the SW's focus behavior, and the cold path opens
 * home (`/`).
 */

/** ONE push categories the backend emits (server/apps/reunification/push_notifications.py). */
export const ONE_PUSH_CATEGORIES = new Set([
  'one_reunification_pickup',
  'one_pickup_delegation',
  'one_student_checkin',
]);

/** Shape of `payload.data` on a ONE push (untrusted wire data — narrow at runtime). */
export interface OnePushClickData {
  type?: string;
  [key: string]: unknown;
}

/**
 * Resolve the in-app route a clicked ONE push notification should land on.
 *
 * @returns the route (e.g. `/events/<public_id>`) when the payload is a known
 *   ONE category carrying a `reunification_event_id`; `null` when there is no
 *   concrete deep target (unknown/missing category, missing/empty id).
 */
export function resolveOnePushClickRoute(
  data: OnePushClickData | null | undefined,
): string | null {
  if (!data?.type || !ONE_PUSH_CATEGORIES.has(data.type)) return null;
  const eventId = data['reunification_event_id'];
  if (typeof eventId !== 'string' || eventId.length === 0) return null;
  return `/events/${encodeURIComponent(eventId)}`;
}
